import { Chip, Link as HeroLink } from '@heroui/react';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { PageLoading } from '../../../../components/PageLoading';
import { useGetBuyer } from '../../hooks';
import { BuyerProfileTabs } from '../BuyerProfileTabs';

import { ProfileTab } from './ProfileTab';
import { SettingsTab } from './SettingsTab';
import { TeamTab } from './TeamTab';

export const BuyerProfile: React.FC = () => {
  const [activeTab, setActiveTab] = useState('profile');

  const { buyer, isLoading: loading, error } = useGetBuyer();

  useEffect(() => {
    if (buyer?.name) {
      document.title = `${buyer.name} - Buyer Profile - Choco Admin`;
    }
  }, [buyer?.name]);

  const renderTabContent = useCallback(() => {
    if (!buyer) return null;

    switch (activeTab) {
      case 'profile':
        return <ProfileTab buyer={buyer} />;
      case 'settings':
        return <SettingsTab buyer={buyer} />;
      case 'team':
        return <TeamTab buyer={buyer} />;
      default:
        return <ProfileTab buyer={buyer} />;
    }
  }, [activeTab, buyer]);

  if (loading) {
    return <PageLoading text="Loading buyer profile..." />;
  }

  if (error || !buyer) {
    return (
      <ErrorCard
        title="Error Loading Buyer"
        message={
          error instanceof Error
            ? error.message
            : 'Buyer not found or could not be loaded'
        }
        backLinkTo="/buyers"
        backLinkText="← Back to Buyers"
      />
    );
  }

  return (
    <div className="p-6 min-h-screen bg-background">
      <div className="mb-6">
        <HeroLink
          as={Link}
          to="/buyers"
          color="primary"
          className="inline-flex items-center gap-2 mb-4"
        >
          ← Back to Buyers
        </HeroLink>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              {buyer.name}
            </h1>
            <div className="flex items-center gap-2">
              <Chip
                variant="flat"
                color="default"
                size="sm"
                classNames={{
                  base: 'bg-default-100 text-default-700',
                  content: 'font-mono text-xs p-2',
                }}
              >
                {buyer.referenceId || 'N/A'}
              </Chip>
              {buyer.status && (
                <Chip
                  variant="flat"
                  color={buyer.status === 'active' ? 'success' : 'default'}
                  size="sm"
                >
                  {buyer.status}
                </Chip>
              )}
              {buyer.isTest && (
                <Chip variant="flat" color="warning" size="sm">
                  Test Buyer
                </Chip>
              )}
            </div>
          </div>
        </div>
      </div>

      <BuyerProfileTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="mt-6">{renderTabContent()}</div>
    </div>
  );
};
